import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiErrorDetail } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login({ register: isRegister }) {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = isRegister ? await register(email, password, name) : await login(email, password);
      nav(u.onboarded ? "/" : "/onboarding");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center"><Leaf className="w-6 h-6 text-white" /></div>
          <div>
            <div className="font-display font-bold text-white">SusTek</div>
            <div className="text-[10px] text-slate-400 font-mono uppercase tracking-wider">Adviser OS</div>
          </div>
        </div>
        <div className="relative">
          <h1 className="font-display text-4xl xl:text-5xl font-bold text-white leading-tight tracking-tight">
            Turn your sustainability expertise into a flourishing, high-impact business.
          </h1>
          <p className="text-slate-400 mt-5 max-w-md leading-relaxed">
            Win more clients, deliver world-class advisory, and grow your revenue — with an AI copilot that drafts, prices, and recommends while you stay firmly in control. Every output is yours to review and approve. Start building the practice you've always wanted.
          </p>
          <div className="flex flex-wrap gap-2 mt-8">
            {["CRM & Pipeline", "AI Diagnostic", "Proposals", "Client Workspace"].map((t) => (
              <span key={t} className="text-xs font-mono px-3 py-1.5 rounded-full bg-slate-800 text-emerald-300 border border-slate-700">{t}</span>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-slate-500 font-mono">Human-in-the-loop · Evidence-labelled · Never presented as assurance</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center"><Leaf className="w-6 h-6 text-white" /></div>
            <div className="font-display font-bold text-slate-900 text-lg">SusTek Adviser OS</div>
          </div>
          <h2 className="font-display text-2xl font-bold text-slate-900">{isRegister ? "Create your adviser account" : "Welcome back"}</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">{isRegister ? "Start your independent practice." : "Sign in to your practice."}</p>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5" placeholder="Amara Chen" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="email-input" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="you@practice.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="password-input" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="••••••••" />
            </div>
            {error && <p data-testid="auth-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="submit-auth" disabled={loading} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : isRegister ? "Create account" : "Sign in"}
            </Button>
          </form>

          {!isRegister && (
            <div className="mt-4 text-xs text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-2.5">
              <span className="font-medium text-slate-700">Demo adviser:</span> admin@sustek.io / SusTek@2026
            </div>
          )}

          <p className="text-sm text-slate-500 mt-6 text-center">
            {isRegister ? (<>Already have an account? <Link to="/login" className="text-emerald-600 font-medium hover:underline">Sign in</Link></>)
              : (<>New adviser? <Link to="/register" data-testid="go-register" className="text-emerald-600 font-medium hover:underline">Create an account</Link></>)}
          </p>
        </motion.div>
      </div>
    </div>
  );
}
